import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useAuth } from "../../../hooks/useAuth";
import { useGiftees } from "../hooks/useGiftees";
import { useReminders } from "../hooks/useReminders";
import { useSavedRecommendations } from "../hooks/useSavedRecommendations";
import {
  openCreateGifteeWizard,
  closeGifteeHub,
} from "../createGifteeWizardNav.js";
import { recommendationCardsForGiftee } from "../utils/giftSuggestions.js";
import { Tags, Button, Text } from "../../../components/ui";
import ReminderRecoCarousel from "../components/ReminderRecoCarousel.jsx";
import AmazonAssociateFooter from "../components/AmazonAssociateFooter.jsx";
import GifteeAppBottomNav from "../components/GifteeAppBottomNav.jsx";
import styles from "../styles/GifteeHub.module.css";

function nextReminderByDate(reminders) {
  if (!reminders?.length) return null;
  const scored = reminders.map((r) => {
    const raw = r.date?.toDate ? r.date.toDate() : new Date(r.date);
    const t = raw.getTime();
    return { reminder: r, t: Number.isNaN(t) ? Infinity : t };
  });
  scored.sort((a, b) => a.t - b.t);
  return scored[0]?.reminder ?? null;
}

export default function GifteeHubScreen() {
  const { gifteeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  useAuth();
  const { giftees, loading: gifteesLoading } = useGiftees();
  const { reminders, loading: remindersLoading } = useReminders(gifteeId);
  const { savedRecommendations } = useSavedRecommendations(gifteeId);

  const [expandedSecondaryIds, setExpandedSecondaryIds] = useState(
    () => new Set(),
  );
  const [primaryCollapsed, setPrimaryCollapsed] = useState(false);

  const giftee = giftees.find((g) => g.id === gifteeId);

  if (gifteesLoading || remindersLoading) {
    return (
      <div className={styles.shell}>
        <div className={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  if (!giftee) {
    return (
      <div className={styles.shell}>
        <div className={styles.loadingState}>
          <p>Giftee not found.</p>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => openCreateGifteeWizard(navigate, "/home")}
          >
            Create a giftee
          </button>
        </div>
      </div>
    );
  }

  const fmtBudget = (min, max) => {
    const fmtVal = (v) => (v >= 200 ? "$200+" : `$${v}`);
    return `${fmtVal(min)}-${fmtVal(max)}`;
  };

  const daysUntil = (ts) => {
    if (!ts) return null;
    const dateObj = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const target = new Date(dateObj);
    target.setHours(0, 0, 0, 0);
    const diff = Math.ceil((target - now) / 86400000);
    if (diff < 0) {
      const nextYear = new Date(
        now.getFullYear() + 1,
        target.getMonth(),
        target.getDate(),
      );
      return Math.ceil((nextYear - now) / 86400000);
    }
    return diff;
  };

  const fmtDateShort = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
  };

  const recommendations = recommendationCardsForGiftee(
    giftee,
    savedRecommendations,
  );

  const spotlightReminder = nextReminderByDate(reminders);
  const spotlightDays = spotlightReminder
    ? daysUntil(spotlightReminder.date)
    : null;
  const urgencyLabel =
    spotlightReminder != null &&
    spotlightDays != null &&
    Number.isFinite(spotlightDays)
      ? `${spotlightReminder.title} in ${spotlightDays} ${spotlightDays === 1 ? "Day" : "Days"}`
      : null;

  const recCountLabel = `${recommendations.length} Recommendations`;

  const sortedReminders = [...reminders].sort((a, b) => {
    const dA = daysUntil(a.date) ?? Infinity;
    const dB = daysUntil(b.date) ?? Infinity;
    return dA - dB;
  });

  const within30Days = sortedReminders.filter((r) => {
    const d = daysUntil(r.date);
    return d !== null && Number.isFinite(d) && d <= 30;
  });
  const beyond30Days = sortedReminders.filter((r) => {
    const d = daysUntil(r.date);
    return d === null || !Number.isFinite(d) || d > 30;
  });

  const primaryReminder = within30Days[0] ?? null;
  const secondaryWithin30 = within30Days.slice(1);
  const secondaryReminders = beyond30Days;
  const primaryDays = primaryReminder ? daysUntil(primaryReminder.date) : null;

  const expandSecondary = (id) => {
    setExpandedSecondaryIds((prev) => new Set(prev).add(id));
  };

  const collapseSecondary = (id) => {
    setExpandedSecondaryIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  return (
    <div className={styles.shell}>
      {/* Main Content - single scrollable area; header is first child so sticky works */}
      <main className={styles.content}>
        {/* Header - sticky within the scroll container so backdrop-filter works */}
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>Account Overview</h1>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => closeGifteeHub(navigate, location)}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className={styles.screen}>
          <section className={styles.profile}>
            <div className={styles.profileStack}>
              {urgencyLabel ? (
                <Tags label={urgencyLabel} color="midnight" variant="filled" />
              ) : null}
              <Text
                as="h1"
                variant="headlineMd"
                align="start"
                className={styles.displayName}
              >
                {giftee.name}
              </Text>
              <div className={styles.profileTags}>
                <Tags
                  label={`Reminders: ${reminders.length}`}
                  color="brandPrimary"
                  variant="filled"
                />
                <Tags
                  label={`Budget: ${fmtBudget(giftee.budgetMin ?? 50, giftee.budgetMax ?? 120)}`}
                  color="brandPrimary"
                  variant="filled"
                />
              </div>
              <div className={styles.editRow}>
                <button
                  type="button"
                  className={styles.editBtn}
                  aria-label={`Giftee details for ${giftee.name}`}
                  onClick={() => navigate(`/giftee/${giftee.id}/info`)}
                >
                  {/* Figma Edit component 393:4319 */}
                  <svg
                    className={styles.editIcon}
                    viewBox="0 0 14.9143 14.9143"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M6.75002 2.16422H2.08334C1.72972 2.16422 1.39058 2.3047 1.14053 2.55475C0.890478 2.8048 0.750002 3.14393 0.750002 3.49756V12.8309C0.750002 13.1845 0.890478 13.5237 1.14053 13.7737C1.39058 14.0238 1.72972 14.1643 2.08334 14.1643H11.4167C11.7703 14.1643 12.1095 14.0238 12.3595 13.7737C12.6096 13.5237 12.75 13.1845 12.75 12.8309V8.16424M11.75 1.16422C12.0153 0.899 12.375 0.750002 12.75 0.750002C13.1251 0.750002 13.4848 0.899 13.75 1.16422C14.0153 1.42943 14.1643 1.78915 14.1643 2.16422C14.1643 2.53929 14.0153 2.89901 13.75 3.16422L7.41669 9.49758L4.75001 10.1642L5.41668 7.49757L11.75 1.16422Z" />
                  </svg>
                  <span className={styles.editLabel}>Edit Giftee</span>
                </button>
              </div>
            </div>
          </section>

          <div className={styles.remindersSection}>
            {reminders.length === 0 && (
              <p className={styles.empty}>No reminders yet. Add one below.</p>
            )}

            {primaryReminder ? (
              <div className={styles.primaryReminderGroup}>
                <div className={styles.reminderSection}>
                  <Text variant="titleSm" className={styles.sectionEyebrow}>
                    In the next 30 days
                  </Text>
                  <section className={styles.reminderCard}>
                    {primaryCollapsed ? (
                      <button
                        type="button"
                        className={styles.collapsedReminderTap}
                        aria-expanded={false}
                        aria-label={`Show gift recommendations for ${primaryReminder.title}`}
                        onClick={() => setPrimaryCollapsed(false)}
                      >
                        <div className={styles.reminderCardHeader}>
                          <h2 className={styles.reminderTitle}>
                            {primaryReminder.title}
                          </h2>
                          <span className={styles.iconGhost} aria-hidden="true">
                            <i className="fa-solid fa-up-right-and-down-left-from-center" />
                          </span>
                        </div>
                        <div className={styles.reminderBadges}>
                          {primaryDays !== null ? (
                            <Tags
                              label={`${primaryDays} ${primaryDays === 1 ? "Day" : "Days"}`}
                              color="brandPrimary"
                              variant="filled"
                            />
                          ) : null}
                          <Tags
                            label={fmtDateShort(primaryReminder.date)}
                            color="brandPrimary"
                            variant="filled"
                          />
                          <Tags
                            label={recCountLabel}
                            color="midnight"
                            variant="filled"
                          />
                        </div>
                      </button>
                    ) : (
                      <>
                        <div className={styles.reminderCardHeader}>
                          <h2 className={styles.reminderTitle}>
                            {primaryReminder.title}
                          </h2>
                          <button
                            type="button"
                            className={styles.iconGhostBtn}
                            aria-expanded
                            aria-label={`Hide gift recommendations for ${primaryReminder.title}`}
                            onClick={() => setPrimaryCollapsed(true)}
                          >
                            <i className="fa-solid fa-down-left-and-up-right-to-center" />
                          </button>
                        </div>
                        <div className={styles.reminderBadges}>
                          {primaryDays !== null ? (
                            <Tags
                              label={`${primaryDays} ${primaryDays === 1 ? "Day" : "Days"}`}
                              color="brandPrimary"
                              variant="filled"
                            />
                          ) : null}
                          <Tags
                            label={fmtDateShort(primaryReminder.date)}
                            color="brandPrimary"
                            variant="filled"
                          />
                          <Tags
                            label={recCountLabel}
                            color="midnight"
                            variant="filled"
                          />
                        </div>
                        <ReminderRecoCarousel
                          reminder={primaryReminder}
                          recommendations={recommendations}
                          gifteeId={gifteeId}
                          navigate={navigate}
                          includeTopRecoPill
                          showDots
                        />
                      </>
                    )}
                  </section>
                </div>

                {secondaryWithin30.map((reminder) => {
                  const days = daysUntil(reminder.date);
                  const isCollapsed = !expandedSecondaryIds.has(reminder.id);
                  return (
                    <div
                      key={reminder.id}
                      className={styles.reminderSection}
                    >
                      <section className={styles.reminderCard}>
                        {isCollapsed ? (
                          <button
                            type="button"
                            className={styles.collapsedReminderTap}
                            aria-expanded={false}
                            aria-label={`Show gift recommendations for ${reminder.title}`}
                            onClick={() => expandSecondary(reminder.id)}
                          >
                            <div className={styles.reminderCardHeader}>
                              <h2 className={styles.reminderTitle}>
                                {reminder.title}
                              </h2>
                              <span
                                className={styles.iconGhost}
                                aria-hidden="true"
                              >
                                <i className="fa-solid fa-up-right-and-down-left-from-center" />
                              </span>
                            </div>
                            <div className={styles.reminderBadges}>
                              {days !== null ? (
                                <Tags
                                  label={`${days} ${days === 1 ? "Day" : "Days"}`}
                                  color="brandPrimary"
                                  variant="filled"
                                />
                              ) : null}
                              <Tags
                                label={fmtDateShort(reminder.date)}
                                color="brandPrimary"
                                variant="filled"
                              />
                              <Tags
                                label={recCountLabel}
                                color="midnight"
                                variant="filled"
                              />
                            </div>
                          </button>
                        ) : (
                          <>
                            <div className={styles.reminderCardHeader}>
                              <h2 className={styles.reminderTitle}>
                                {reminder.title}
                              </h2>
                              <button
                                type="button"
                                className={styles.iconGhostBtn}
                                aria-expanded
                                aria-label={`Hide gift recommendations for ${reminder.title}`}
                                onClick={() => collapseSecondary(reminder.id)}
                              >
                                <i className="fa-solid fa-down-left-and-up-right-to-center" />
                              </button>
                            </div>
                            <div className={styles.reminderBadges}>
                              {days !== null ? (
                                <Tags
                                  label={`${days} ${days === 1 ? "Day" : "Days"}`}
                                  color="brandPrimary"
                                  variant="filled"
                                />
                              ) : null}
                              <Tags
                                label={fmtDateShort(reminder.date)}
                                color="brandPrimary"
                                variant="filled"
                              />
                              <Tags
                                label={recCountLabel}
                                color="midnight"
                                variant="filled"
                              />
                            </div>
                            <ReminderRecoCarousel
                              reminder={reminder}
                              recommendations={recommendations}
                              gifteeId={gifteeId}
                              navigate={navigate}
                              includeTopRecoPill
                              showDots
                            />
                          </>
                        )}
                      </section>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {secondaryReminders.length > 0 ? (
              <div className={styles.otherCelebrationsGroup}>
                <Text variant="titleSm" className={styles.sectionEyebrow}>
                  Other Celebrations
                </Text>

                <div className={styles.otherCelebrationsList}>
                  {secondaryReminders.map((reminder) => {
                    const days = daysUntil(reminder.date);
                    const isSecondaryCollapsed = !expandedSecondaryIds.has(
                      reminder.id,
                    );

                    return (
                      <div key={reminder.id} className={styles.reminderSection}>
                        <section className={styles.reminderCard}>
                          {isSecondaryCollapsed ? (
                            <button
                              type="button"
                              className={styles.collapsedReminderTap}
                              aria-expanded={false}
                              aria-label={`Show gift recommendations for ${reminder.title}`}
                              onClick={() => expandSecondary(reminder.id)}
                            >
                              <div className={styles.reminderCardHeader}>
                                <h2 className={styles.reminderTitle}>
                                  {reminder.title}
                                </h2>
                                <span
                                  className={styles.iconGhost}
                                  aria-hidden="true"
                                >
                                  <i className="fa-solid fa-up-right-and-down-left-from-center" />
                                </span>
                              </div>
                              <div className={styles.reminderBadges}>
                                {days !== null ? (
                                  <Tags
                                    label={`${days} ${days === 1 ? "Day" : "Days"}`}
                                    color="brandPrimary"
                                    variant="filled"
                                  />
                                ) : null}
                                <Tags
                                  label={fmtDateShort(reminder.date)}
                                  color="brandPrimary"
                                  variant="filled"
                                />
                                <Tags
                                  label={recCountLabel}
                                  color="midnight"
                                  variant="filled"
                                />
                              </div>
                            </button>
                          ) : (
                            <>
                              <div className={styles.reminderCardHeader}>
                                <h2 className={styles.reminderTitle}>
                                  {reminder.title}
                                </h2>
                                <button
                                  type="button"
                                  className={styles.iconGhostBtn}
                                  aria-expanded
                                  aria-label={`Hide gift recommendations for ${reminder.title}`}
                                  onClick={() => collapseSecondary(reminder.id)}
                                >
                                  <i className="fa-solid fa-down-left-and-up-right-to-center" />
                                </button>
                              </div>
                              <div className={styles.reminderBadges}>
                                {days !== null ? (
                                  <Tags
                                    label={`${days} ${days === 1 ? "Day" : "Days"}`}
                                    color="brandPrimary"
                                    variant="filled"
                                  />
                                ) : null}
                                <Tags
                                  label={fmtDateShort(reminder.date)}
                                  color="brandPrimary"
                                  variant="filled"
                                />
                                <Tags
                                  label={recCountLabel}
                                  color="midnight"
                                  variant="filled"
                                />
                              </div>

                              <ReminderRecoCarousel
                                reminder={reminder}
                                recommendations={recommendations}
                                gifteeId={gifteeId}
                                navigate={navigate}
                                includeTopRecoPill
                                showDots
                              />
                            </>
                          )}
                        </section>
                      </div>
                    );
                  })}
                </div>

                <div className={styles.addReminderWrap}>
                  <Button
                    variant="secondary"
                    fullWidth
                    label="New Celebration +"
                    onClick={() =>
                      navigate(`/giftee/${gifteeId}/info?section=celebrations`)
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {/* .content already applies 16px side padding; use a smaller inset here
            so the total left/right gap matches GifteeHomeScreen's unpadded
            scroll container and the disclosure stays on a single line. */}
        <AmazonAssociateFooter inset={8} />
      </main>
      <GifteeAppBottomNav />
    </div>
  );
}
